import { useEffect } from 'react';
import type { SitePage, SitePageSummary, VisitorConfig } from '../../types';
import { getFontOption, googleFontsUrl } from '../../utils/fonts';
import { SectionRenderer } from './SectionRenderer';
import { SiteFooter } from './SiteFooter';
import { SiteWidgets } from './SiteWidgets';

/**
 * Injecte la balise <link> Google Fonts correspondant à la police choisie.
 * Autorisé par la CSP existante (style-src/font-src, voir server/app.ts).
 */
function useSiteFont(fontKey: string | undefined) {
  useEffect(() => {
    const option = getFontOption(fontKey);
    if (!option) return;
    const id = `site-font-${option.key}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = googleFontsUrl(option);
    document.head.appendChild(link);
  }, [fontKey]);
}

export function SiteHeader({ config, menuPages }: { config: VisitorConfig; menuPages: SitePageSummary[] }) {
  return (
    <header className="border-b border-gray-100 sticky top-0 bg-white/95 backdrop-blur z-40">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2">
          {config.brandLogoUrl ? (
            <img
              src={config.brandLogoUrl}
              alt={config.brandLogoAlt || config.brandLogoText}
              style={{ height: config.brandLogoHeightPx || 40 }}
            />
          ) : (
            <span className="font-bold text-lg" style={{ color: config.brandColor }}>
              {config.brandLogoText}
            </span>
          )}
        </a>
        <nav className="flex items-center gap-5 text-sm font-medium">
          <a href="/" className="hover:opacity-70">
            Accueil
          </a>
          {(config.customMenuLinks || []).map((link) => (
            <a key={link.id} href={link.target} className="hover:opacity-70">
              {link.label}
            </a>
          ))}
          {menuPages.map((page) => (
            <a key={page.id} href={`/${page.slug}`} className="hover:opacity-70">
              {page.title}
            </a>
          ))}
          <a href="/blog" className="hover:opacity-70">
            Blog
          </a>
        </nav>
      </div>
    </header>
  );
}

interface PageRendererProps {
  page: SitePage;
  config: VisitorConfig;
  menuPages: SitePageSummary[];
}

/**
 * Rendu générique d'une page composée de sections (texte, image, slider,
 * séparateur). Utilisé pour toute page créée via l'éditeur, y compris
 * l'accueil dès qu'elle a du contenu — voir src/App.tsx pour la bascule.
 */
export function PageRenderer({ page, config, menuPages }: PageRendererProps) {
  useSiteFont(config.typography?.fontFamily);
  const fontOption = getFontOption(config.typography?.fontFamily);

  return (
    <div
      className="min-h-screen bg-white"
      style={fontOption ? { fontFamily: fontOption.cssFamily } : undefined}
    >
      <SiteHeader config={config} menuPages={menuPages} />
      <main className="max-w-6xl mx-auto flex flex-wrap">
        {page.sections.map((section) => (
          <SectionRenderer key={section.id} section={section} />
        ))}
      </main>
      <SiteFooter config={config} menuPages={menuPages} />
      <SiteWidgets config={config} />
    </div>
  );
}
